import { makeStyles } from '@material-ui/styles'
import { useCallback, useMemo } from 'react'
import LoadingTextWithSpinner from 'core/components/LoadingTextWithSpinner'
import BadgeWithIcon from 'core/elements/badge/BadgeWithIcon'
import Tooltip from 'core/elements/tooltip/Tooltip'
import type Theme from 'core/themes/model'
import { deriveVmVmhaStatus, mapVMHAConditions } from './helpers'
import { type IClusterHostGroup, VMHADisplayStatus } from './model'
import VmhaConditionsStatus from './VmhaConditionsStatus'

// FR-VSC-006: per-VM label differs from the cluster-level "Disabled" text
const vmStatusLabel: Partial<Record<string, string>> = {
  [VMHADisplayStatus.DISABLED]: 'VM HA disabled',
}

export const VMHAStatusForClusterList = ({
  value,
  item,
  loading,
}: {
  value: string
  item: IClusterHostGroup
  loading?: boolean
}) => {
  const classes = useStyles()

  const getBadgeVariant = useCallback(() => {
    switch (value) {
      case VMHADisplayStatus.PROTECTED:
        return 'success'
      case VMHADisplayStatus.NOT_PROTECTED:
        return 'error'
      case VMHADisplayStatus.DEGRADED:
        return 'warning'
      case VMHADisplayStatus.DISABLED:
        return 'default'
      default:
        return 'default'
    }
  }, [value])

  const getBadgeIcon = useCallback(() => {
    switch (value) {
      case VMHADisplayStatus.PROTECTED:
        return 'check'
      case VMHADisplayStatus.NOT_PROTECTED:
        return 'times'
      case VMHADisplayStatus.DEGRADED:
        return 'exclamation-triangle'
      default:
        return undefined
    }
  }, [value])

  // Return Loading State
  if (loading || value === VMHADisplayStatus.LOADING) {
    return <LoadingTextWithSpinner />
  }

  return (
    <Tooltip
      className={classes.vmhaStatusTooltipWrapper}
      customBody={
        <VmhaConditionsStatus
          forTooltip
          title={'VMHA Conditions'}
          conditions={mapVMHAConditions(item?.vmhaState)}
        />
      }
    >
      <BadgeWithIcon variant={getBadgeVariant()} text={value} icon={getBadgeIcon()} bold={true} />
    </Tooltip>
  )
}

export const VMHAStatusCellForVMsList = ({ value, item, clusterHostGroups, blueprint }) => {
  const status = useMemo(
    () => deriveVmVmhaStatus(clusterHostGroups, item, blueprint),
    [clusterHostGroups, item, blueprint],
  )

  const getBadgeVariant = useCallback(() => {
    switch (status) {
      case VMHADisplayStatus.PROTECTED:
        return 'success'
      case VMHADisplayStatus.NOT_PROTECTED:
        return 'error'
      case VMHADisplayStatus.DEGRADED:
        return 'warning'
      case VMHADisplayStatus.DISABLED:
        return 'default'
      default:
        return 'default'
    }
  }, [status])

  const getBadgeIcon = useCallback(() => {
    switch (status) {
      case VMHADisplayStatus.PROTECTED:
        return 'check'
      case VMHADisplayStatus.NOT_PROTECTED:
        return 'times'
      case VMHADisplayStatus.DEGRADED:
        return 'exclamation-triangle'
      default:
        return undefined
    }
  }, [status])

  return (
    <BadgeWithIcon
      variant={getBadgeVariant()}
      text={vmStatusLabel[status] ?? status}
      icon={getBadgeIcon()}
      bold={true}
    />
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => {
  return {
    vmhaStatusTooltipWrapper: {
      width: 'fit-content',
    },
  }
})
